/**
 * The route table.
 *
 * `react-router-dom` was chosen over a hand-rolled router for one reason that matters here:
 * this application is both the Control Centre and the UM app PWA on one origin (stance D2),
 * so it will carry nested layouts, deep links from an email or a phone, and eventually a
 * service worker. A router that already handles history, relative links and route params
 * correctly is a smaller liability than eighty lines of our own that will grow to do the
 * same job less well.
 *
 * Every module in the strip is routed, and as of run 34 every one of them resolves to a built
 * screen: `StubPage` is now reached only by the catch-all `*`, where an unknown path is a
 * genuinely unbuilt destination rather than a module waiting its turn.
 *
 * **Module 00 is built.** `/` is spec 01's Command Desk — the home surface, composed entirely
 * from the routes the other modules already serve. It adds no route of its own, deliberately:
 * every figure on it is a view over fixtures, officials, appointments, hearings and report
 * progress, and an aggregate endpoint would be a second place for each of those numbers to be
 * computed.
 *
 * **Module 02 is built.** `/appointments/umpires` is spec 03's Part B — the supervision
 * surface, with the Umpires Managers' appointments read-only on one half and the engine's
 * advisory draft on the other, and the two structurally incapable of crossing (Part C
 * invariant 5).
 *
 * **Module 03 is built.** `/fixtures` is spec 02's whole schedule: both competitions, grouped
 * by match day, one TMS status per fixture, and a status key that doubles as the filter. It
 * is the last read-only surface the other modules lean on — appointments bind to a fixture,
 * hearings name one, the Command Desk reads today from one — so it is drawn from the same
 * `GET /fixtures` they all read and it hides nothing, finished matches included.
 *
 * **Module 04 is built as its first slice.** `/hearings` is spec 06's case record and
 * document pipeline; document generation, the Code of Conduct catalogue, evidence, the
 * decision record, the sanction tracker and the two legal clocks are not built, and the page
 * says so where the specification puts each of them.
 *
 * **Module 05 is routed twice and both halves are now built.** `/officials` is the roster of
 * `04-officials-profiles.md` Part A and `/officials/:officialId` is the individual profile of
 * Part B, built first so that a roster row would have a real profile to open — which is what
 * the panel note has always promised.
 *
 * **Two route trees, and the split matters.** `/sign-in` is the only path outside the
 * session gate, because it is the one way through it — the personal link lands there with
 * its token in the fragment. Everything else is behind `RequireSession`, which sends a
 * signed-out device there instead of drawing a Shell full of panels that would each 401 on
 * their own — carrying the destination it stopped, so it can be resumed (gap J9).
 */

import { useEffect } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";

import { AppointmentsPage } from "../pages/AppointmentsPage.js";
import { CommandDeskPage } from "../pages/CommandDeskPage.js";
import { ConfigurationPage } from "../pages/ConfigurationPage.js";
import { FixturesPage } from "../pages/FixturesPage.js";
import { HearingsPage } from "../pages/HearingsPage.js";
import { MarkingPage } from "../pages/MarkingPage.js";
import { OfficialProfilePage } from "../pages/OfficialProfilePage.js";
import { OfficialsRosterPage } from "../pages/OfficialsRosterPage.js";
import { ReportStudioPage } from "../pages/ReportStudioPage.js";
import { ReportingPage } from "../pages/ReportingPage.js";
import { SIGN_IN_PATH, SignInPage, type SignInLandingState } from "../pages/SignInPage.js";
import { StubPage } from "../pages/StubPage.js";
import { TdMatchDebriefPage } from "../pages/TdMatchDebriefPage.js";
import { UmpireAppointmentsPage } from "../pages/UmpireAppointmentsPage.js";
import { UmCapturePage } from "../pages/um/UmCapturePage.js";
import { UmDebriefDocumentPage } from "../pages/um/UmDebriefDocumentPage.js";
import { UmDebriefPage } from "../pages/um/UmDebriefPage.js";
import { UmMarkingPage } from "../pages/um/UmMarkingPage.js";
import { UmMatchDebriefRoute } from "../pages/um/UmMatchDebriefPage.js";
import { UmMyNotesPage } from "../pages/um/UmMyNotesPage.js";
import { UmReportPage } from "../pages/um/UmReportPage.js";
import { UmUmpiresPage } from "../pages/um/UmUmpiresPage.js";
import { UmTodayPage } from "../pages/um/UmTodayPage.js";
import { UM_GUIDE_PATH } from "./HowToUse.js";
import { Shell } from "./Shell.js";
import { UmShell } from "./UmShell.js";
import { signInPathWithNext } from "./resume.js";
import { SessionProvider, useSession } from "./session.js";
import { FailBanner, Skeleton } from "./states.js";

/**
 * The gate every module sits behind.
 *
 * It wraps the Shell rather than living inside it, so a signed-out person gets a screen and
 * not a masthead with a module strip they cannot use. `/sign-in` is outside it, because the
 * one way through the gate is a link that lands there.
 *
 * **A signed-out device is SENT to `/sign-in`, and the destination goes with it** (gap J9,
 * spec 09 §4). It used to be drawn in place: the person saw the right screen and the address
 * bar still said `/marking/9901`, but nothing remembered it, so signing in landed them on the
 * Command Desk with the row they were sent to gone. The redirect is a `replace`, so the
 * history entry that could not be shown does not become a Back button that cannot be either.
 *
 * **Why the destination and the reason travel differently.** The destination is a path, and
 * it goes in the URL where a reload can still find it. The reason — expired, signed out,
 * never signed in — is not the URL's business and would be a needlessly public statement
 * about this device, so it rides in the history entry's state and falls back to
 * `no_credential` if it is lost. That is the same screen the person would otherwise be
 * looking at, so nothing they need is ever missing.
 */
function RequireSession(): React.JSX.Element {
  const { session } = useSession();
  const location = useLocation();

  if (session.status === "signed-out") {
    const landing: SignInLandingState = { reason: session.reason };
    return (
      <Navigate
        to={signInPathWithNext(`${location.pathname}${location.search}`)}
        state={landing}
        replace
      />
    );
  }
  if (session.status === "unreachable") {
    return (
      <div className="signedout">
        <section className="panel panel--d1 signedout__panel">
          <div className="panel__head">
            <h3 className="panel__title">The Control Centre Could Not Be Reached</h3>
            <span className="panel__note">Still Signed In</span>
          </div>
          <FailBanner error={session.error} />
          <div className="panel__foot">
            This device still holds its credential — nothing has been signed out. Reload once
            the connection is back.
          </div>
        </section>
      </div>
    );
  }
  if (session.status === "checking") return <Skeleton rows={4} />;
  return <Outlet />;
}

/**
 * Forward `/guide` to the hosted PDF. A hard `location.replace` rather than a `Navigate`,
 * because the destination is a file the SERVER serves, not a route this router knows — a
 * client-side navigation to it would land on the catch-all stub. `replace` keeps the
 * redirecting page out of history, so Back returns to wherever the reader came from.
 */
function GuideRedirect(): null {
  useEffect(() => {
    window.location.replace(UM_GUIDE_PATH);
  }, []);
  return null;
}

export function App(): React.JSX.Element {
  return (
    <SessionProvider>
      <Routes>
        <Route path={SIGN_IN_PATH} element={<SignInPage />} />
        {/*
         * `/guide` — the short address printed in the UM Onboarding Guide's own footer
         * ("hockeytd.org/guide"). The PDF itself is a static file the server hands out at
         * `/guide/UM-Onboarding-Guide.pdf` without ever waking this router; this route exists
         * for the person who TYPES the short form, and simply forwards them to the file.
         * It sits OUTSIDE the session gate on purpose: the guide is handed to managers before
         * some of them have redeemed a sign-in link, and a manual that demanded a sign-in to
         * read about signing in would be the wrong kind of joke. (Run 76.)
         */}
        <Route path="guide" element={<GuideRedirect />} />
        <Route element={<RequireSession />}>
          {/*
           * The UM app — one origin and one sign-in with the Control Centre, but its own
           * shell: `Shell` is the laptop masthead and module strip, and the UM app is a
           * phone screen with a three-item bottom bar (um-app/00-overview.md §10). Both
           * sit behind the same session gate because there is one credential.
           * `UmShell` explains at length where the UM app lives and why.
           */}
          <Route path="um" element={<UmShell />}>
            <Route index element={<UmTodayPage />} />
            <Route path="match/:tmsMatchDbId" element={<UmCapturePage />} />
            {/*
             * The Match Debrief screen (spec 13 §3, run 73) — the post-match debrief with the
             * match's U1, U2 and Video Umpire.
             *
             * A CHILD of the match path and keyed on the same TMS database match id, so the two
             * screens about one match are one path apart and the back arrow can name its target.
             * The competition travels in the query for the reason it travels to capture: a
             * debrief response is a note and a note must carry one.
             */}
            <Route path="match/:tmsMatchDbId/debrief" element={<UmMatchDebriefRoute />} />
            {/*
             * Every note this manager has written, tournament-wide — the TD's own finding of
             * 2026-09-09, that the app had nowhere to read a note back from. A READ ONLY: the
             * presses that change a note (edit, lock, release, the §H warning) stay on the
             * capture screen above, which owns spec 03's release semantics; this page links to
             * it per match rather than rebuilding one of them.
             */}
            <Route path="notes" element={<UmMyNotesPage />} />
            {/*
             * The §B3b surface: the manager's own umpires, and for each of them the final
             * marking screen and the Report Studio. Keyed on the TMS person id, never on a
             * name or a slug — the same key the API's ownership check reads.
             */}
            <Route path="umpires" element={<UmUmpiresPage />} />
            <Route path="umpires/:tmsPersonId/marking" element={<UmMarkingPage />} />
            <Route path="umpires/:tmsPersonId/report" element={<UmReportPage />} />
            {/*
             * The Daily Debrief Pack (spec 05), in the deterministic slice `UmDebriefPage`
             * documents at length: the pack is computed when the tab is opened rather than
             * generated to a schedule, and the AI one-liners, the flags that need sentiment, the
             * `Discussed` mark and the A4 document are absent rather than stubbed.
             *
             * ~~and the A4 document~~ **SUPERSEDED 2026-09-09.** Spec 07's document is built at
             * `debrief/print`, as a print-styled page rather than the stored server-side PDF
             * §3 describes; `UmDebriefDocumentPage` states that difference in its own header
             * and names every element of spec 07 it does not carry. It is a CHILD of the pack's
             * path rather than a sibling, so the bottom bar keeps `Debrief` lit while a manager
             * is looking at the paper, and it takes its matchday from `?day=` so the link is
             * one that can be kept and re-opened.
             */}
            <Route path="debrief" element={<UmDebriefPage />} />
            <Route path="debrief/print" element={<UmDebriefDocumentPage />} />
          </Route>
          <Route element={<Shell />}>
            <Route index element={<CommandDeskPage />} />
            <Route path="marking" element={<ReportingPage />} />
            <Route path="marking/:officialId" element={<MarkingPage />} />
            <Route path="marking/:officialId/report" element={<ReportStudioPage />} />
            <Route path="appointments/technical" element={<AppointmentsPage />} />
            <Route path="appointments/umpires" element={<UmpireAppointmentsPage />} />
            <Route path="fixtures" element={<FixturesPage />} />
            {/*
             * The Technical Delegate's READ-ONLY Match Debrief (spec 13 §9, run 75).
             *
             * A CHILD of the Fixtures path, so `moduleForPath` lights module 03 in the strip and
             * names it in the breadcrumb: the screen is about one fixture, and the Control Centre
             * already has the module that lists every fixture there is. It claims no strip entry
             * of its own — an eight-entry nav taken verbatim from the design is not where a
             * per-match window belongs.
             *
             * It carries NO competition in the query, unlike the UM app's screen at
             * `/um/match/:tmsMatchDbId/debrief`. That screen writes notes and a note must carry a
             * competition; this one writes nothing, so it needs nothing.
             */}
            <Route path="fixtures/:tmsMatchDbId/debrief" element={<TdMatchDebriefPage />} />
            <Route path="hearings" element={<HearingsPage />} />
            <Route path="officials" element={<OfficialsRosterPage />} />
            <Route path="officials/:officialId" element={<OfficialProfilePage />} />
            <Route path="configuration" element={<ConfigurationPage />} />
            <Route path="*" element={<StubPage />} />
          </Route>
        </Route>
      </Routes>
    </SessionProvider>
  );
}
